<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = withDefaults(defineProps<{
  cut?: string | string[];
  mobileMode?: boolean;
  stoneType?: string;
}>(), {
  cut: "ex",
  mobileMode: false,
  stoneType: 'earth',
});

const host = process.env.VUE_APP_HOST;

const allCuts = [
  {
    key: "gd",
    label: "Good",
    img: "/img/diamonds/details/cut_gd.webp",
  },
  {
    key: "vg",
    label: "Very Good",
    img: "/img/diamonds/details/cut_vg.webp",
  },
  {
    key: "ex",
    label: "Excellent",
    img: "/img/diamonds/details/cut_ex.webp",
  },
  {
    key: "id",
    label: "Ideal",
    img: "/img/diamonds/details/cut_id.webp",
  },
];

const cuts = computed(() => {
  if (props.stoneType === "earth") {
    return allCuts.filter((c) => c.key !== "id");
  }
  return allCuts;
});

const getNormalizedCut = (c: string) => {
  if (!c) return "ex";
  c = c.toLowerCase();
  if (c === "id" || c === "ideal") return "id";
  if (c === "ex" || c === "excellent") return "ex";
  if (c === "vg" || c === "very good") return "vg";
  if (c === "gd" || c === "good") return "gd";
  return c;
};

const getInitialIndex = () => {
  const cutValue = Array.isArray(props.cut) ? props.cut[0] : props.cut;
  if (!cutValue) return cuts.value.findIndex((c) => c.key === "ex") ?? 0;
  const normalized = getNormalizedCut(cutValue);
  const index = cuts.value.findIndex((c) => c.key === normalized);
  return index !== -1 ? index : (cuts.value.findIndex((c) => c.key === "ex") ?? 0);
};

const selectedIndex = ref(getInitialIndex());

const isMobile = ref(window.innerWidth < 769);
const updateMobile = () => {
  isMobile.value = window.innerWidth < 769;
};

const isMobileMode = computed(() => props.mobileMode || isMobile.value);

watch(() => [props.cut, props.stoneType], () => {
  selectedIndex.value = getInitialIndex();
});

const sliderRef = ref<HTMLElement | null>(null);
const isDragging = ref(false);

const updateIndexFromEvent = (e: MouseEvent | TouchEvent) => {
  if (!sliderRef.value) return;
  const rect = sliderRef.value.getBoundingClientRect();
  const clientX =
    "touches" in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
  const offsetX = clientX - rect.left;
  let percentage = offsetX / rect.width;
  percentage = Math.max(0, Math.min(0.99, percentage));
  selectedIndex.value = Math.floor(percentage * cuts.value.length);
};

const startDragging = (e: MouseEvent | TouchEvent) => {
  isDragging.value = true;
  updateIndexFromEvent(e);
};

const handleMove = (e: MouseEvent | TouchEvent) => {
  if (isDragging.value) {
    updateIndexFromEvent(e);
  }
};

const stopDragging = () => {
  isDragging.value = false;
};

onMounted(() => {
  window.addEventListener("mousemove", handleMove);
  window.addEventListener("mouseup", stopDragging);
  window.addEventListener("touchmove", handleMove, { passive: false });
  window.addEventListener("touchend", stopDragging);
  window.addEventListener("resize", updateMobile);
});

onUnmounted(() => {
  window.removeEventListener("mousemove", handleMove);
  window.removeEventListener("mouseup", stopDragging);
  window.removeEventListener("touchmove", handleMove);
  window.removeEventListener("touchend", stopDragging);
  window.removeEventListener("resize", updateMobile);
});
</script>

<template>
  <div :class="['flex flex-col items-center ', isMobileMode ? '' : 'md:gap-12 md:flex-row']">
    <!-- Left column: info text -->
    <div :class="['w-full text-left', isMobileMode ? '' : 'md:w-1/3']">
      <slot name="title" :selectedLabel="cuts[selectedIndex]?.label">
        <h3 class="text-2xl font-bold mb-6">
          {{ t("components.diamonds.details.cut.title") }}:
          {{ cuts[selectedIndex]?.label }}
        </h3>
      </slot>
      <p class="text-black font-light text-xs leading-relaxed whitespace-pre-line">
        {{ t("components.diamonds.details.cut.content_text") }}
      </p>
    </div>

    <!-- Right column: Slider -->
    <div :class="['w-full', isMobileMode ? '' : 'md:w-2/3']">
      <div class="flex flex-col">
        <!-- Images -->
        <div :class="['flex justify-center items-end mb-6', isMobileMode ? '' : 'md:justify-between']">
          <div
            v-for="(c, index) in cuts"
            :key="c.key"
            class="justify-center px-2 flex"
            :class="[
              selectedIndex === index ? 'flex' : (isMobileMode ? 'hidden' : 'hidden md:flex'),
              isMobileMode ? '' : 'md:flex-1',
            ]"
          >
            <img
              :src="host + c.img"
              :alt="c.label"
              class="transition-opacity duration-300 cursor-pointer "
              :class="[
                isMobileMode ? 'h-52 w-80 object-cover max-w-[420px]' : 'max-w-[420px] md:max-w-[140px]',
                { 'md:opacity-90': !isMobileMode && selectedIndex !== index }
              ]"
              @click="selectedIndex = index"
            />
          </div>
        </div>

        <!-- Slider -->
        <div
          class="relative pb-32 cursor-pointer"
          ref="sliderRef"
          @mousedown="startDragging"
          @touchstart.prevent="startDragging"
        >
          <div class="flex gap-1.5 h-1">
            <div
              v-for="(c, index) in cuts"
              :key="'bar-' + c.key"
              class="flex-1 cursor-pointer relative group"
              @click="selectedIndex = index"
            >
              <!-- Active/Inactive Bar -->
              <div
                class="h-full w-full rounded-full transition-colors duration-300"
                :class="
                  selectedIndex === index ? 'bg-brand-main' : 'bg-brand-main/30'
                "
              ></div>

              <!-- Label -->
              <span
                class="absolute top-2 left-1/2 -translate-x-1/2 text-sm transition-colors duration-300 whitespace-nowrap"
                :class="
                  selectedIndex === index
                    ? 'text-brand-main font-medium'
                    : 'text-gray-400'
                "
              >
                {{ c.label }}
              </span>
            </div>
          </div>

          <!-- Description Bubble -->
          <div class="absolute left-0 right-0 mt-10 transition-all duration-300">
            <div class="bg-brand-main rounded-xl p-2 text-white text-center relative">
              <!-- Triangle pointing up -->
            <div
                class="absolute -top-2 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[10px] border-b-brand-main transition-all duration-300"
                :style="{ left: `calc(${selectedIndex * (100 / cuts.length) + (100 / cuts.length) / 2}% - 10px)` }"
              ></div>
              <p class="text-xs leading-relaxed">
                {{ t("components.diamonds.details.cut.descriptions." + cuts[selectedIndex]?.key) }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
